import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Archive, Layers, Gem, Store, Loader2 } from "lucide-react";
import { zoomCard } from "@/components/CardZoom";

const STATUS_META = {
  vault: { label: "IN VAULT", color: "#A1A1AA" },
  pool: { label: "IN PACK POOL", color: "#A3E635" },
  one_of_one: { label: "ONE-OF-ONE", color: "#F87171" },
  market: { label: "ON MARKETPLACE", color: "#22D3EE" },
};

export default function PersonalVaultPanel() {
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(null);
  const [marketFor, setMarketFor] = useState(null);
  const [price, setPrice] = useState("10");
  const [copies, setCopies] = useState("3");

  const load = () => api.get("/admin/vault/personal").then(({ data }) => setCards(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const place = async (c, action, extra = {}) => {
    setBusy(`${c.id}-${action}`);
    try {
      await api.post(`/admin/vault/personal/${c.id}/place`, { action, ...extra });
      const msg = {
        vault: `"${c.name}" pulled back into your Personal Vault`,
        pool: `"${c.name}" released into the pack pool`,
        one_of_one: `"${c.name}" is now a ONE-OF-ONE hiding in packs 🚨`,
        market: `"${c.name}" listed on the Marketplace`,
      }[action];
      toast.success(msg);
      setMarketFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (cards.length === 0) return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="personal-vault-panel">
      <div className="flex items-center gap-2 mb-1"><Archive className="w-4 h-4 text-pb-red" /><span className="font-display font-bold uppercase">Personal Vault</span></div>
      <p className="text-xs text-zinc-600">Cards you forge appear here. Mint one in the Card Forge with "Hold in Vault" to stock your vault, then release it whenever you're ready.</p>
    </div>
  );

  return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="personal-vault-panel">
      <div className="flex items-center gap-2 mb-1"><Archive className="w-4 h-4 text-pb-red" /><span className="font-display font-bold uppercase">Personal Vault — Your Forged Cards ({cards.length})</span></div>
      <p className="text-xs text-zinc-500 mb-4">
        Every card you've forged lives here. Hold it, drop it into the pack pool, make it a true one-of-one (only ONE copy
        will ever be pulled), or list copies on the Marketplace at your price.
      </p>
      <div className="max-h-[480px] overflow-y-auto divide-y divide-pb-border/40">
        {cards.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.vault;
          return (
            <div key={c.id} className="py-3" data-testid={`pv-row-${c.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <img src={c.url} alt="" className="w-12 aspect-[3/4] object-contain bg-black border border-pb-border cursor-zoom-in"
                  title="Tap to zoom" onClick={() => zoomCard(c.url, c.subject_name)} />
                <div className="w-40 min-w-0">
                  <div className="text-xs font-semibold truncate">{c.name}</div>
                  <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border inline-block mt-1"
                    style={{ color: meta.color, borderColor: `${meta.color}66`, background: `${meta.color}14` }}
                    data-testid={`pv-status-${c.id}`}>
                    {meta.label}{c.status === "one_of_one" && c.pulled ? " · PULLED" : ""}
                  </span>
                  <div className="text-[9px] text-zinc-600 mt-1">{c.copies_owned} in circulation{c.listings > 0 ? ` · ${c.listings} listed` : ""}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {c.status !== "vault" && (
                    <button data-testid={`pv-vault-${c.id}`} onClick={() => place(c, "vault")} disabled={!!busy}
                      className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest border border-pb-border text-zinc-300 px-2.5 py-2 hover:border-zinc-400 transition-colors disabled:opacity-50">
                      {busy === `${c.id}-vault` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />} Hold in Vault
                    </button>
                  )}
                  {c.status !== "pool" && (
                    <button data-testid={`pv-pool-${c.id}`} onClick={() => place(c, "pool")} disabled={!!busy}
                      className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest border border-lime-400/50 text-lime-400 px-2.5 py-2 hover:bg-lime-400/10 transition-colors disabled:opacity-50">
                      {busy === `${c.id}-pool` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />} To Pack Pool
                    </button>
                  )}
                  {c.status !== "one_of_one" && c.copies_owned === 0 && (
                    <button data-testid={`pv-ooo-${c.id}`} onClick={() => place(c, "one_of_one")} disabled={!!busy}
                      className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest border border-red-400/50 text-red-400 px-2.5 py-2 hover:bg-red-400/10 transition-colors disabled:opacity-50">
                      {busy === `${c.id}-one_of_one` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gem className="w-3 h-3" />} Drop as 1/1
                    </button>
                  )}
                  {c.status !== "market" && (
                    <button data-testid={`pv-market-${c.id}`} onClick={() => setMarketFor(marketFor === c.id ? null : c.id)} disabled={!!busy}
                      className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest border border-pb-cyan/50 text-pb-cyan px-2.5 py-2 hover:bg-pb-cyan/10 transition-colors disabled:opacity-50">
                      <Store className="w-3 h-3" /> To Marketplace
                    </button>
                  )}
                </div>
              </div>
              {marketFor === c.id && (
                <div className="flex flex-wrap items-end gap-3 mt-2.5 border border-pb-cyan/30 bg-pb-cyan/5 p-3" data-testid={`pv-market-form-${c.id}`}>
                  <div>
                    <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Price (BLC)</label>
                    <input data-testid={`pv-market-price-${c.id}`} type="number" min={0.5} step={0.5} value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="w-24 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-pb-cyan" />
                  </div>
                  <div>
                    <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Copies</label>
                    <input data-testid={`pv-market-copies-${c.id}`} type="number" min={1} max={100} step={1} value={copies}
                      onChange={(e) => setCopies(e.target.value)}
                      className="w-20 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-pb-cyan" />
                  </div>
                  <button data-testid={`pv-market-confirm-${c.id}`} disabled={!!busy}
                    onClick={() => place(c, "market", { price: Number(price) || 0, copies: Number(copies) || 1 })}
                    className="flex items-center gap-1.5 bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-4 py-2 hover:bg-[#BEF264] transition-colors disabled:opacity-50">
                    {busy === `${c.id}-market` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Store className="w-3 h-3" />} List {copies} @ {price} BLC
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
